import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import SEO from "@/components/seo";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const Imprint = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={embedded ? '' : 'min-h-screen'} style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="Imprint"
        description="Legal information and contact details for BrandComply. Company registration, responsible parties, and regulatory disclosures."
        path="/imprint"
      />
      {!embedded && (
        <header style={{ background: PALETTE.gunmetal }} className="text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div
                className="flex items-center space-x-1 cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                  BrandComply
                </div>
              </div>

              <Button
                variant="outline"
                className="font-semibold w-fit rounded-full border-2 h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                style={{ borderColor: PALETTE.inchworm, color: PALETTE.inchworm, background: 'transparent' }}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </header>
      )}

      <main className={embedded ? '' : 'max-w-4xl mx-auto px-6 py-16'}>
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
            Imprint 
          </h1>
        </div>

        <div className="prose max-w-none space-y-8">
          <section>
            <div className="space-y-2 text-base" style={{ color: `${PALETTE.gunmetal}80` }}>
              <p className="font-semibold text-lg" style={{ color: PALETTE.gunmetal }}>BrandComply</p>                      
              <p>St.Gallen</p>
              <p>Switzerland</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Contact
            </h2>
            <div className="space-y-2 text-base" style={{ color: `${PALETTE.gunmetal}80` }}>
              <p>
                <span className="font-medium" style={{ color: PALETTE.gunmetal }}>Email:</span>{' '}
                <a href="mailto:info@brandcomply.ai" className="underline hover:no-underline">
                  info@brandcomply.ai
                </a>
              </p>
              <p>
                <span className="font-medium" style={{ color: PALETTE.gunmetal }}>Website:</span>{' '}
                <a href="https://brandcomply.ai" className="underline hover:no-underline" target="_blank" rel="noopener noreferrer">
                  https://brandcomply.ai
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Responsible for Content
            </h2>
            <div className="space-y-1 text-base" style={{ color: `${PALETTE.gunmetal}80` }}>
              <p>BrandComply</p>
              <p>St.Gallen</p>
              <p>Switzerland</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Liability for Content
            </h2>
            <p className="text-base leading-relaxed" style={{ color: `${PALETTE.gunmetal}80` }}>
              The content of our pages was created with great care. However, we cannot guarantee the accuracy, completeness, or timeliness of the content. As a service provider, we are responsible for our own content on these pages under applicable Swiss law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Liability for Links
            </h2>
            <p className="text-base leading-relaxed" style={{ color: `${PALETTE.gunmetal}80` }}>
              Our website contains links to external third-party websites over whose content we have no control. Therefore, we cannot accept any liability for external content. The respective provider or operator of the linked pages is always responsible for their content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              Copyright
            </h2>
            <p className="text-base leading-relaxed" style={{ color: `${PALETTE.gunmetal}80` }}>
              The content and works created by the site operator on these pages are subject to copyright law. Any reproduction, processing, distribution, or use outside the limits of copyright law requires the prior written consent of BrandComply.
            </p>
          </section>
        </div>
      </main>

      {!embedded && (
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-16">
              <div className="max-w-xs">
                <div
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => navigate('/')}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  Launch influencer & UGC video campaigns faster and on-brand.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Product</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">How it Works</button>
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">Pricing</button>
                  <button onClick={() => navigate('/api')} className="block text-white/70 hover:text-white transition-colors text-left">API</button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Legal</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/terms-of-service')} className="block text-white/70 hover:text-white transition-colors text-left">Terms of Service</button>
                  <button onClick={() => navigate('/privacy-policy')} className="block text-white/70 hover:text-white transition-colors text-left">Privacy Policy</button>
                  <button onClick={() => navigate('/imprint')} className="block text-white/70 hover:text-white transition-colors text-left">Imprint</button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Imprint;
